import React, { useEffect, useState } from 'react';
import leaveAPI from '../api/leaveAPI';
import UserDashboard from './UserDashboard';

const LeaveFormWithTable = () => {
  const [showForm, setShowForm] = useState(false);
  const [leaveType, setLeaveType] = useState("Full Day");
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [fromTime, setFromTime] = useState('');
  const [toTime, setToTime] = useState('');
  const [reason, setReason] = useState('');
  const [message, setMessage] = useState('');
  const [worker, setWorker] = useState(null);
  const [leaveData, setLeaveData] = useState([]);

  useEffect(() => {
    const stored = localStorage.getItem('worker');
    if (stored) {
      const parsed = JSON.parse(stored);
      setWorker(parsed);
      fetchLeaves(parsed._id);
    }
  }, []);

  const fetchLeaves = async (workerId) => {
    try {
      const res = await leaveAPI.get(`?workerId=${workerId}`);
      setLeaveData(res.data);
    } catch (err) {
      console.error('Error fetching leave data:', err);
    }
  };

  const handleSubmit = async () => {
    try {
      const validWorkModes = ['Remote', 'Onsite', 'Hybrid'];
      const selectedWorkMode = validWorkModes.includes(worker?.workMode)
        ? worker.workMode
        : 'Onsite';

      // Prepare payload with team info
      let payload = {
        workerId: worker._id,
        workerName: worker.username,
        profileImage: worker.profileImage,
        role: worker.role,
        company: worker.company,
        workMode: selectedWorkMode,
        reason,
        leaveType,
        team: worker.team, 
      };

      if (leaveType === "Full Day") {
        payload.fromDate = fromDate;
        payload.toDate = toDate;
      } else {
        payload.fromTime = fromTime;
        payload.toTime = toTime;
      }

      await leaveAPI.post('/', payload);

      alert('Leave applied successfully');
      setShowForm(false);
      setFromDate('');
      setToDate('');
      setFromTime('');
      setToTime('');
      setReason('');
      fetchLeaves(worker._id);
    } catch (err) {
      console.error('❌ Leave submission error:', err?.response?.data || err.message);
      alert('Submission failed: ' + (err?.response?.data?.message || err.message));
    }
  };

  return (
    <div className="p-6 bg-gradient-to-b from-purple-50 to-pink-50 min-h-screen pt-28">
      <UserDashboard />

      {/* FORM */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <form
            className="bg-white p-6 rounded-xl shadow-lg w-[400px]"
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
          >
            <div className="flex items-center mb-4">
              <img
                src={worker?.profileImage?.replace(/\\/g, '/') || 'default.png'}
                alt="Profile"
                className="w-14 h-14 rounded-full mr-3 border border-purple-300"
              />
              <div>
                <p className="font-semibold text-lg text-purple-700">{worker?.username}</p>
                <p className="text-sm text-gray-500">{worker?.role}</p>
              </div>
            </div>

            {/* Leave Type */}
            <label className="block text-purple-700 font-medium mb-1">Leave Type</label>
            <select
              value={leaveType}
              onChange={(e) => setLeaveType(e.target.value)}
              className="border rounded-md p-2 w-full mb-3"
            >
              <option value="Full Day">Full Day Leave</option>
              <option value="Permission">Permission (Hours)</option>
            </select>

            {/* Conditional Inputs */}
            {leaveType === "Full Day" ? (
              <>
                <label className="block text-purple-700 font-medium mb-1">From Date</label>
                <input
                  type="date"
                  className="border rounded-md p-2 w-full mb-3"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  required
                />
                <label className="block text-purple-700 font-medium mb-1">To Date</label>
                <input
                  type="date"
                  className="border rounded-md p-2 w-full mb-3"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  required
                />
              </>
            ) : (
              <>
                <label className="block text-purple-700 font-medium mb-1">From Time</label>
                <input
                  type="time"
                  className="border rounded-md p-2 w-full mb-3"
                  value={fromTime}
                  onChange={(e) => setFromTime(e.target.value)}
                  required
                />
                <label className="block text-purple-700 font-medium mb-1">To Time</label>
                <input
                  type="time"
                  className="border rounded-md p-2 w-full mb-3"
                  value={toTime}
                  onChange={(e) => setToTime(e.target.value)}
                  required
                />
              </>
            )}

            {/* Reason */}
            <label className="block text-purple-700 font-medium mb-1">Reason</label>
            <textarea
              placeholder="Enter reason"
              className="border rounded-md p-2 w-full mb-4"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
            />

            <button
              type="submit"
              className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded w-full mb-2"
            >
              Submit
            </button>

            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="text-red-500 underline w-full text-sm"
            >
              Cancel
            </button>
          </form>
        </div>
      )}

      {/* TABLE */}
      <div className="max-w-7xl mx-auto bg-white shadow-md rounded-xl p-4">
        <div className="flex justify-between items-center mb-3">
          <h2 className="text-3xl font-bold text-purple-700 font-mono">Leave Report</h2>
          <button
            onClick={() => setShowForm(true)}
            className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-lg shadow-md"
          >
            + Apply Leave
          </button>
        </div>

        <div className="overflow-x-auto">
          <div className="max-h-[450px] overflow-y-auto">
            <table className="min-w-full table-auto border border-purple-200 rounded-md">
              <thead className="bg-purple-100 text-purple-800 sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-4">Applied Date</th>
                  <th className="px-4 py-4">Profile</th>
                  <th className="px-4 py-4">Name</th>
                  <th className="px-4 py-4">Role</th>
                  <th className="px-4 py-4">Work Mode</th>
                  <th className="px-4 py-4">Leave Type</th>
                  <th className="px-4 py-4">From</th>
                  <th className="px-4 py-4">To</th>
                  <th className="px-4 py-4">Duration</th>
                  <th className="px-4 py-4">Reason</th>
                  <th className="px-4 py-4">Status</th>
                </tr>
              </thead>
              <tbody>
                {leaveData.length > 0 ? (
                  leaveData.map((leave) => (
                    <tr key={leave._id} className="text-center text-gray-700 border-t">
                      <td className="px-4 py-4">{new Date(leave.createdAt).toLocaleDateString('en-IN')}</td>
                      <td className="px-4 py-4">
                        <img
                          src={leave?.profileImage?.replace(/\\/g, '/') || 'default.png'}
                          alt="Profile"
                          className="w-8 h-8 rounded-full mx-auto border border-purple-300"
                        />
                      </td>
                      <td className="px-4 py-4">{leave.workerName}</td>
                      <td className="px-4 py-4">{leave.role}</td>
                      <td className="px-4 py-4">{leave.workMode}</td>
                      <td className="px-4 py-4">{leave.leaveType}</td>
                      {leave.leaveType === "Full Day" ? (
                        <>
                          <td className="px-4 py-4">{new Date(leave.fromDate).toLocaleDateString('en-GB')}</td>
                          <td className="px-4 py-4">{new Date(leave.toDate).toLocaleDateString('en-GB')}</td>
                        </>
                      ) : (
                        <>
                          <td className="px-4 py-4">{leave.fromTime}</td>
                          <td className="px-4 py-4">{leave.toTime}</td>
                        </>
                      )}
                      <td className="px-4 py-4">{leave.duration}</td>
                      <td className="px-4 py-4">{leave.reason}</td>
                      <td className={`px-4 py-4 font-semibold ${
                        leave.status === 'Approved'
                          ? 'text-green-600'
                          : leave.status === 'Rejected'
                          ? 'text-red-600'
                          : 'text-yellow-600'
                      }`}>
                        {leave.status}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="11" className="text-center py-4 text-gray-500">
                      No leave records
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {message && (
        <div className="mt-4 text-center text-green-600 font-medium">{message}</div>
      )}
    </div>
  );
};

export default LeaveFormWithTable;
